// Path: /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/pages/Contact.tsx
// Version: v1.0.0
// Beschreibung: Kontaktseite mit Formular, Standort-Karte und Ansprechpartnern

import { useState } from "react";
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from "lucide-react";
import HeroBanner from "../components/HeroBanner.tsx";
import ContactPerson from "../components/ContactPerson.tsx";
import AnchorMenu from "../components/AnchorMenu.tsx";
import Container from "../hooks/Container.tsx";
import { useLanguage } from "../lang/index.lang.tsx";

// Formular-State Interface
interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

// Formular-Status
type FormStatus = "idle" | "sending" | "success" | "error";

const Contact = () => {
  const { t } = useLanguage();
  
  // Form State
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  
  const [formStatus, setFormStatus] = useState<FormStatus>("idle");
  const [errors, setErrors] = useState<Partial<ContactFormData>>({});

  // Validierung
  const validateForm = (): boolean => {
    const newErrors: Partial<ContactFormData> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t("contact.form.errors.nameRequired");
    }
    
    if (!formData.email.trim()) {
      newErrors.email = t("contact.form.errors.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("contact.form.errors.emailInvalid");
    }
    
    if (!formData.subject) {
      newErrors.subject = t("contact.form.errors.subjectRequired");
    }
    
    if (!formData.message.trim()) {
      newErrors.message = t("contact.form.errors.messageRequired");
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form Submit Handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setFormStatus("sending");
    
    // Mailto-Link erstellen
    const subject = encodeURIComponent(`[${formData.subject}] Anfrage von ${formData.name}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\nE-Mail: ${formData.email}\n\nNachricht:\n${formData.message}`
    );
    
    // E-Mail öffnen
    window.location.href = `mailto:info@estata.com?subject=${subject}&body=${body}`;
    
    // Success nach kurzer Verzögerung
    setTimeout(() => {
      setFormStatus("success");
      setFormData({ name: "", email: "", subject: "", message: "" });
      
      // Reset nach 3 Sekunden
      setTimeout(() => setFormStatus("idle"), 3000);
    }, 500);
  };

  // Input Handler
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof ContactFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Betreff-Optionen
  const subjectOptions = [
    { value: "", label: t("contact.form.subjectPlaceholder") },
    { value: "general", label: t("contact.form.subjects.general") },
    { value: "project", label: t("contact.form.subjects.project") },
    { value: "career", label: t("contact.form.subjects.career") },
    { value: "press", label: t("contact.form.subjects.press") },
    { value: "other", label: t("contact.form.subjects.other") },
  ];

  return (
    <>
      {/* Anchor Menu */}
      <AnchorMenu
        title={t("contact.title")}
        titleLink="#contact"
        items={[
          { id: "1", label: t("contact.sections.info"), anchor: "ContactInfo" },
          { id: "2", label: t("contact.sections.form"), anchor: "ContactForm" },
          { id: "3", label: t("contact.sections.location"), anchor: "Location" },
          { id: "4", label: t("contact.sections.team"), anchor: "ContactPersons" },
        ]}
      />

      {/* Hero Banner */}
      <section id="contact">
        <HeroBanner
          image="/images/EST-1344212168.jpg"
          subtitle={t("contact.subtitle")}
          title={t("contact.title")}
          description={t("contact.description")}
          buttonText={t("contact.cta")}
          buttonLink="#ContactForm"
        />
      </section>

      {/* Kontakt-Informationen */}
      <section id="ContactInfo" className="py-16 md:py-24">
        <Container>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-[#37acdc] font-medium mb-2">{t("contact.info.subtitle")}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t("contact.info.title")}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                {t("contact.info.description")}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Adresse */}
              <div className="text-center p-6">
                <div className="w-12 h-12 bg-[#37acdc]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-6 h-6 text-[#37acdc]" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{t("contact.info.address")}</h3>
                <p className="text-gray-600">
                  ESTATA GmbH<br />
                  Katharinenstraße 9<br />
                  10711 Berlin
                </p>
              </div>

              {/* Telefon */}
              <div className="text-center p-6">
                <div className="w-12 h-12 bg-[#37acdc]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-6 h-6 text-[#37acdc]" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{t("contact.info.phone")}</h3>
                <a href="tel:+493062987730" className="text-gray-600 hover:text-[#37acdc] transition-colors">
                  +49 30 62987730
                </a>
              </div>

              {/* E-Mail */}
              <div className="text-center p-6">
                <div className="w-12 h-12 bg-[#37acdc]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6 text-[#37acdc]" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{t("contact.info.email")}</h3>
                <a href="mailto:info@estata.com" className="text-gray-600 hover:text-[#37acdc] transition-colors">
                  info@estata.com
                </a>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Kontaktformular */}
      <section id="ContactForm" className="py-16 md:py-24 bg-gray-50">
        <Container>
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-[#37acdc] font-medium mb-2">{t("contact.form.subtitle")}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t("contact.form.title")}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8">
              {/* Name */}
              <div className="mb-6">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  {t("contact.form.name")} *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t("contact.form.namePlaceholder")}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  } focus:ring-2 focus:ring-[#37acdc] focus:border-transparent transition-colors`}
                />
                {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
              </div>

              {/* E-Mail */}
              <div className="mb-6">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t("contact.form.email")} *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t("contact.form.emailPlaceholder")}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  } focus:ring-2 focus:ring-[#37acdc] focus:border-transparent transition-colors`}
                />
                {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
              </div>

              {/* Betreff */}
              <div className="mb-6">
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  {t("contact.form.subject")} *
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.subject ? "border-red-500" : "border-gray-300"
                  } focus:ring-2 focus:ring-[#37acdc] focus:border-transparent transition-colors`}
                >
                  {subjectOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.subject && <p className="mt-1 text-sm text-red-500">{errors.subject}</p>}
              </div>

              {/* Nachricht */}
              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  {t("contact.form.message")} *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  placeholder={t("contact.form.messagePlaceholder")}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.message ? "border-red-500" : "border-gray-300"
                  } focus:ring-2 focus:ring-[#37acdc] focus:border-transparent transition-colors resize-none`}
                />
                {errors.message && <p className="mt-1 text-sm text-red-500">{errors.message}</p>}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={formStatus === "sending"}
                className="w-full bg-[#37acdc] hover:bg-[#2d8db5] text-white font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {formStatus === "sending" ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t("contact.form.sending")}
                  </>
                ) : formStatus === "success" ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    {t("contact.form.success")}
                  </>
                ) : formStatus === "error" ? (
                  <>
                    <AlertCircle className="w-5 h-5" />
                    {t("contact.form.error")}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    {t("contact.form.submit")}
                  </>
                )}
              </button>

              <p className="mt-4 text-sm text-gray-500 text-center">
                {t("contact.form.privacy")}
              </p>
            </form>
          </div>
        </Container>
      </section>

      {/* Standort / Google Maps */}
      <section id="Location" className="py-16 md:py-24">
        <Container>
          <div className="text-center mb-12">
            <p className="text-[#37acdc] font-medium mb-2">{t("contact.location.subtitle")}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              {t("contact.location.title")}
            </h2>
          </div>
          
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2427.5!2d13.3!3d52.49!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47a850e1f!2sKatharinenstra%C3%9Fe%209%2C%2010711%20Berlin!5e0!3m2!1sde!2sde!4v1"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="ESTATA Standort"
              className="grayscale hover:grayscale-0 transition-all duration-500"
            />
          </div>
        </Container>
      </section>

      {/* Ansprechpartner */}
      <section id="ContactPersons">
        <ContactPerson
          heading={{
            subtitle: t("contact.team.subtitle"),
            title: t("contact.team.title"),
          }}
          employeeIds={["SR", "AS"]}
        />
      </section>
    </>
  );
};

export default Contact;
